import { Injectable, NgZone } from '@angular/core';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { DateTime } from 'luxon';
import { MarsGroupService } from 'app/mars/services/mars-groups.service';

@Injectable()
export class MarsGroupActivityListService {
  userRole: any;
  license: any;
  dataSource: any;
  mode: string = 'view';
  constructor(
    public marsGroupService: MarsGroupService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public ngZone: NgZone
  ) {}

  async getDatasource(listId, options: any) {
    this.dataSource = new TableDatasource({
      sort: '-accumulatedPoints',
      columns: this.getColumns(),
      load: (props) => this.fetch(props, this.dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.profileService.getLoggedUserRole().name}`
      },
      ...options
    });
    await this.dataSource.refreshData();
    StoreService.create(listId, this.dataSource);
    return this.dataSource;
  }

  fetch({ page, limit, sort, search, filters }, dataSource: TableDatasource) {
    const { groupId, session, turn, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      groupId,
      session,
      turn,
      $filter
    };
    return this.marsGroupService.getGroupActivity(groupId, paginationOptions).then((response) => response);
  }

  getColumns() {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('nouns.name'),
        dataType: DataTypes.String,
        sortable: true,
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'surname',
        label: this.translate.instant('nouns.surname'),
        dataType: DataTypes.String,
        sortable: true,
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'userEmail',
        label: this.translate.instant('nouns.email'),
        dataType: DataTypes.String,
        sortable: true,
        cellRenderer: (renderer, value) => renderer`<div class="ellipsis" title="${value}">${value}</div>`
      },
      {
        source: 'accumulatedPoints',
        label: this.translate.instant('reports.points'),
        sortable: true,
        allowFiltering: false,
        valueParser: (value) => {
          return value == null ? '' : '' + value;
        }
      },
      {
        source: 'session',
        style: { width: '80px' },
        label: this.translate.instant('mars.session'),
        allowFiltering: false
      },
      {
        source: 'turn',
        style: { width: '80px' },
        label: this.translate.instant('reports.turn'),
        allowFiltering: false
      },
      {
        source: 'startDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.firstConnection'),
        sortable: true,
        allowFiltering: false,
        dataType: DataTypes.datetime,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'endDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.lastConnection'),
        sortable: true,
        allowFiltering: false,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'status',
        style: { width: '120px' },
        label: this.translate.instant('nouns.status'),
        sortable: false,
        valueParser: (value) => this.translate.instant(`mars.player.status.${value}`),
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'created', value: this.translate.instant('mars.player.status.created') },
            { key: 'active', value: this.translate.instant('mars.player.status.active') },
            { key: 'completed', value: this.translate.instant('mars.player.status.completed') },
            { key: 'cancelled', value: this.translate.instant('mars.player.status.cancelled') }
          ];
          return renderer`
            <gl-select small multiselect
              .options="${options}"
              .value="${value || 'any'}"
              @onChange="${(e) => callback(e.detail.value)}"
            >
            </gl-select>`;
        }
      },
      {
        source: 'gameProgress',
        style: { width: '80px' },
        label: this.translate.instant('reports.gameProgress'),
        allowFiltering: false,
        sortable: true,
        valueParser: (value) => {
          const progress = value ? Math.round(value) : 0;
          return progress + '%';
        }
      }
    ].filter((column) => !!column);
    return columns;
  }

  getCustomFilters() {
    return {};
  }
}
